import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import {
	ACTION_NAME_LABEL_KEY,
	ACTION_NAME_TONE,
	BULK_UPDATE_ACTION_NAME,
	formatActivityLogWhen,
} from "../../pages/ActivityLog/GroupedActivityLog/activityLogGroupedDisplay";
import type { GroupedActivityLogEntry } from "../../pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { AccentIconTile } from "../UIToolkit/molecules/AccentIconTile";
import { EventListItem } from "../UIToolkit/molecules/EventListItem";
import { ShiftFieldChanges } from "./ShiftFieldChanges";
import { actionIcon, describeEntry } from "./shiftHistoryDisplay";

type Props = {
	entry: GroupedActivityLogEntry;
	isLatest: boolean;
	dataTestId: string;
};

/** One Activity Log transaction that wrote the shift: what happened, the shift as it was left, who and when. */
export const ShiftHistoryEntry: FC<Props> = ({ entry, isLatest, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const timezone = chosenDepartment.timezone;
	const when = formatActivityLogWhen(entry.changedAt, timezone);
	const actionName = entry.kind === "resolved" ? entry.actionName : BULK_UPDATE_ACTION_NAME;
	const Icon = actionIcon(actionName);
	const leading = (
		<AccentIconTile size="xs" tone={ACTION_NAME_TONE[actionName]}>
			<Icon />
		</AccentIconTile>
	);

	if (entry.kind === "deferred") {
		return (
			<EventListItem
				leading={leading}
				title={t(ACTION_NAME_LABEL_KEY[actionName])}
				text={
					<>
						{t("activityLog.grouped.deferred.label", { count: entry.groupSize })}
						<div>{t("shiftHistory.change.bulkNoDetails")}</div>
					</>
				}
				meta={when}
				emphasis={isLatest}
				dataTestId={dataTestId}
			/>
		);
	}

	const { what, summary, meta } = describeEntry(entry, timezone, t);
	const changes = entry.shiftChanges !== null && entry.shiftChanges.length > 0 ? entry.shiftChanges : null;
	return (
		<EventListItem
			leading={leading}
			title={what}
			text={
				changes === null ? (
					entry.what === "Shift Updated" ? (
						<>
							{summary}
							<div>{t("shiftHistory.change.noDetails")}</div>
						</>
					) : summary === null ? undefined : (
						summary
					)
				) : (
					<>
						{summary}
						<ShiftFieldChanges changes={changes} dataTestId={`${dataTestId}-changes`} />
					</>
				)
			}
			meta={meta}
			emphasis={isLatest}
			dataTestId={dataTestId}
		/>
	);
};
